import { useState } from "react";
import { apiFetch } from "../../lib/config";

interface DataPoint {
  ts: number;
  value: number;
}

interface MetricsResponse {
  resolution: string;
  datapoints: DataPoint[];
}

const CHART_W = 600;
const CHART_H = 200;
const PAD = 20;

function SVGLineChart({ datapoints }: { datapoints: DataPoint[] }) {
  if (datapoints.length === 0) {
    return (
      <div className="ac-empty">
        <div className="ac-empty__title">No data points to display</div>
      </div>
    );
  }

  const values = datapoints.map((d) => d.value);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const minTs = datapoints[0].ts;
  const maxTs = datapoints[datapoints.length - 1].ts;
  const tsRange = maxTs - minTs || 1;
  const valRange = maxVal - minVal || 1;

  const toX = (ts: number) =>
    PAD + ((ts - minTs) / tsRange) * (CHART_W - PAD * 2);
  const toY = (val: number) =>
    CHART_H - PAD - ((val - minVal) / valRange) * (CHART_H - PAD * 2);

  const points = datapoints
    .map((d) => `${toX(d.ts).toFixed(1)},${toY(d.value).toFixed(1)}`)
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      width="100%"
      height={CHART_H}
      aria-label="Metrics line chart"
      style={{ display: "block" }}
    >
      <polyline
        points={points}
        fill="none"
        stroke="var(--aptean-accent)"
        strokeWidth="2"
      />
      {datapoints.map((d, i) => (
        <circle
          key={i}
          cx={toX(d.ts)}
          cy={toY(d.value)}
          r="3"
          fill="var(--aptean-accent)"
        >
          <title>{`${d.value.toFixed(2)} at ${new Date(d.ts * 1000).toLocaleString()}`}</title>
        </circle>
      ))}
    </svg>
  );
}

export function MetricsPage() {
  const [data, setData] = useState<MetricsResponse | null>(null);
  const [service, setService] = useState("");
  const [metric, setMetric] = useState("");
  const [resolution, setResolution] = useState("5m");
  const [loading, setLoading] = useState(false);

  async function loadMetrics() {
    if (!service || !metric) return;
    setLoading(true);
    const params = new URLSearchParams({ service, metric, resolution });
    const r = await apiFetch(`/api/v1/telemetry/metrics/?${params.toString()}`);
    if (r.ok) setData(await r.json());
    setLoading(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    loadMetrics();
  }

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">Metrics Explorer</h1>
          <p className="ac-page-subtitle">Query and visualize service metrics over time</p>
        </div>
      </div>

      <form className="ac-filter-bar" onSubmit={handleSubmit}>
        <input
          type="text"
          className="aptean-input"
          placeholder="Service name"
          value={service}
          onChange={(e) => setService(e.target.value)}
          aria-label="Service name"
          style={{ minWidth: 180 }}
        />
        <input
          type="text"
          className="aptean-input"
          placeholder="Metric name"
          value={metric}
          onChange={(e) => setMetric(e.target.value)}
          aria-label="Metric name"
          style={{ minWidth: 180 }}
        />
        <select
          className="aptean-input"
          value={resolution}
          onChange={(e) => setResolution(e.target.value)}
          aria-label="Resolution"
        >
          <option value="5m">5 minutes</option>
          <option value="1h">1 hour</option>
          <option value="1d">1 day</option>
        </select>
        <button type="submit" className="aptean-btn aptean-btn--primary" disabled={loading || !service || !metric}>
          Query
        </button>
      </form>

      {loading && (
        <div style={{ padding: "3rem", textAlign: "center", color: "var(--aptean-text-secondary)" }}>Loading...</div>
      )}

      {!loading && !data && (
        <div className="ac-table-wrap">
          <div className="ac-empty">
            <div className="ac-empty__icon">&#9636;</div>
            <div className="ac-empty__title">No query run yet</div>
            <div className="ac-empty__body">Enter a service and metric name to query data.</div>
          </div>
        </div>
      )}

      {data && (
        <div className="ac-table-wrap" style={{ padding: "1.25rem" }}>
          <div style={{ fontSize: "0.8125rem", color: "var(--aptean-text-secondary)", marginBottom: "0.75rem" }}>
            Resolution: <strong>{data.resolution}</strong> &mdash; {data.datapoints.length} data point{data.datapoints.length !== 1 ? "s" : ""}
          </div>
          <SVGLineChart datapoints={data.datapoints} />
        </div>
      )}
    </div>
  );
}
